// The unit plan: how a domain unit is split into weekly ASSIGNMENTS the
// teacher schedules, and how "where should a student be by now" derives.
//
// Model (Sandy 2026-07-14, v2): a unit's work is segmented per concept. Each
// concept group = its own short overview → warm-up → lesson → practice, and
// each group is ONE assignment. The final week is its own assignment: mixed
// practice + the skill check, which also leaves that week open for the
// teacher to intervene with anyone who fails (every unit has at least two
// concepts, so the final week always exists). Weeks can be excused (breaks),
// pushing later due dates back; a week can hold several assignments; each
// week is class time or homework (homework is never judged on minutes).
//
// Known concern (flagged, not solved): units differ in concept count even
// within a domain, so weekly allotments differ per student. Buffer weeks if
// it bites.

import { conceptsFor } from '../../StudentDashboardV3/data/levelConcepts';

// The unit's assignments: one per concept, then the final mixed + check.
// `band` = the level whose concepts the unit covers.
export function assignmentsFor(domainKey, band) {
  const concepts = conceptsFor(domainKey, band || 4);
  return [
    ...concepts.map((con, i) => ({ key: 'c' + i, label: con.name, kind: 'concept', index: i })),
    // The unit always ends with Mixed Practice then a Skill Check, each its own
    // assignment (Sandy 2026-07-16). Never combined into one.
    { key: 'mixed', label: 'Mixed Practice', kind: 'mixed', index: concepts.length },
    { key: 'check', label: 'Skill Check', kind: 'check', index: concepts.length + 1 },
  ];
}

// Default weekly plan: concept assignments spread over every week but the
// last; the final assignment owns the last week (intervention buffer).
export function defaultPlan(unit) {
  const assigns = assignmentsFor(unit.domain, unit.targetBand);
  const weeks = Array.from({ length: unit.total }, () => ({ assignments: [], excused: false, mode: 'class' }));
  const workWeeks = Math.max(1, unit.total - 1);
  const concepts = assigns.filter((a) => a.kind === 'concept');
  concepts.forEach((a, i) => {
    const w = Math.min(workWeeks - 1, Math.floor((i * workWeeks) / concepts.length));
    weeks[w].assignments.push(a.key);
  });
  weeks[unit.total - 1].assignments.push('mixed', 'check');
  return weeks;
}

// Teacher edits persist per class in localStorage (mock; API later).
const planKey = (clsId) => `tdv3.plan3.${clsId}`;

export function loadPlan(clsId, unit) {
  try {
    const raw = localStorage.getItem(planKey(clsId));
    if (raw) {
      const plan = JSON.parse(raw);
      if (Array.isArray(plan) && plan.length === unit.total && plan.every((w) => Array.isArray(w.assignments))) return plan;
    }
  } catch { /* fall through to default */ }
  return defaultPlan(unit);
}

export function savePlan(clsId, plan) {
  try { localStorage.setItem(planKey(clsId), JSON.stringify(plan)); } catch { /* mock */ }
}

// Fridays anchor due dates: week k of the unit is due the k-th Friday from
// the unit's start. Derived backwards from "today is in week `unit.week`".
export function weekDueDate(unit, weekIndex) {
  const now = new Date();
  const fri = new Date(now);
  fri.setDate(now.getDate() + ((5 - now.getDay() + 7) % 7)); // this week's Friday
  fri.setDate(fri.getDate() + (weekIndex + 1 - unit.week) * 7);
  return fri;
}

export const shortDate = (d) => d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

// ---- due-date semantics (2026-08-07) ----
// A stored due may be date-only ('2026-08-21', legacy) or a full ISO datetime
// ('2026-08-22T02:30:00.000Z') when the teacher picked a time. Frontend rule,
// used by every past-due check: dueEndMs(due) = the moment the due closes —
// the exact timestamp when a time is present, otherwise LOCAL end of that day
// (23:59:59.999 local), so a date-only due is never "past" while its calendar
// day is still going for the viewer. Accepts a Date too (mock data).
export const dueHasTime = (due) => typeof due === 'string' && due.includes('T');
export const dueEndMs = (due) => {
  if (!due) return NaN;
  if (due instanceof Date) return due.getTime();
  const s = String(due);
  return s.includes('T') ? Date.parse(s) : Date.parse(s + 'T23:59:59.999');
};
// The Date a due renders as: the exact moment for datetimes, local midnight
// for date-only strings (keeps the calendar day right in every timezone).
export const dueDateOf = (due) => {
  if (due instanceof Date) return due;
  return dueHasTime(due) ? new Date(due) : new Date(due + 'T00:00:00');
};
// 'Fri, Aug 21, 12:30 PM' when a time was picked; date-only dues show their
// implied deadline — 'Fri, Aug 21, 11:59 PM' — so students and teachers always
// see a time (Sandy 2026-08-08). Accepts the raw stored string or a Date.
export const shortDue = (due) => {
  if (!due) return '';
  const d = dueDateOf(due);
  if (Number.isNaN(d.getTime())) return '';
  return dueHasTime(due)
    ? `${shortDate(d)}, ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
    : `${shortDate(d)}, 11:59 PM`;
};

// Every assignment in the plan with its real due date (excused weeks push
// later dues back). `past` = its week is already behind us.
export function allAssignments(unit, plan) {
  const weeks = plan || defaultPlan(unit);
  const byKey = Object.fromEntries(assignmentsFor(unit.domain, unit.targetBand).map((a) => [a.key, a]));
  const out = [];
  let shift = 0;
  weeks.forEach((w, i) => {
    if (w.excused) { shift += 1; return; }
    const effectiveWeek = i + 1 + shift;
    w.assignments.forEach((key) => {
      const a = byKey[key];
      if (!a) return;
      out.push({
        ...a,
        week: i + 1,
        due: weekDueDate(unit, Math.min(effectiveWeek, unit.total) - 1),
        mode: w.mode,
        past: effectiveWeek < unit.week,
      });
    });
  });
  return out.sort((a, b) => a.index - b.index);
}

// Plan "as of now": assignments already due, the next one coming due, and the
// expected completion % (due assignments ÷ all assignments).
export function planStatus(unit, plan) {
  const weeks = plan || defaultPlan(unit);
  const all = allAssignments(unit, plan);
  const dueSoFar = all.filter((a) => a.past);
  const current = all.find((a) => !a.past) || null;
  const totalCount = assignmentsFor(unit.domain, unit.targetBand).length;
  const expectedPct = totalCount
    ? Math.round((dueSoFar.length / totalCount) * 100)
    : 0;
  return { weeks, all, dueSoFar, current, expectedPct };
}
